// Analytics Configuration for Missing SaaS Funnel Measurements
export interface UserSegment {
  type: 'prospect' | 'existing_user' | 'authenticated_user';
  source: 'extension' | 'web_app' | 'mobile';
  plan_type?: 'free' | 'basic' | 'standard' | 'pro';
}

export interface FeatureAdoptionMetric {
  feature_name: string;
  adoption_rate: number;
  time_to_first_use: number;
  usage_frequency: number;
  last_used: string;
}

export interface ConversionFunnelStage {
  stage: 'visitor' | 'signup' | 'onboarded' | 'feature_user' | 'engaged_user' | 'qualified_lead' | 'trial_user' | 'paid_user';
  timestamp: string;
  properties: Record<string, any>;
}

export interface ProductQualifiedLead {
  user_id: string;
  score: number;
  triggers: string[];
  features_used: string[];
  usage_depth: number;
  engagement_level: 'low' | 'medium' | 'high';
}

// Track authenticated vs anonymous users
export const setUserSegment = (segment: UserSegment) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('set', 'user_properties', {
      user_segment: segment.type,
      user_source: segment.source,
      plan_type: segment.plan_type || 'unknown',
    });
  }
};

// Feature adoption tracking
export const trackFeatureAdoption = (metric: FeatureAdoptionMetric) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'feature_adoption', {
      event_category: 'product_adoption',
      feature_name: metric.feature_name,
      adoption_rate: metric.adoption_rate,
      time_to_first_use: metric.time_to_first_use,
      usage_frequency: metric.usage_frequency,
      last_used: metric.last_used,
    });
  }
};

// Product Qualified Lead scoring
export const trackPQL = (pql: ProductQualifiedLead) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'product_qualified_lead', {
      event_category: 'conversion',
      user_id: pql.user_id,
      pql_score: pql.score,
      triggers: pql.triggers.join(','),
      features_used: pql.features_used.join(','),
      usage_depth: pql.usage_depth,
      engagement_level: pql.engagement_level,
    });
  }
};

// Conversion funnel stage tracking
export const trackFunnelStage = (stage: ConversionFunnelStage) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'funnel_progression', {
      event_category: 'conversion_funnel',
      funnel_stage: stage.stage,
      timestamp: stage.timestamp,
      ...stage.properties,
    });
  }
};

// Extension-specific tracking
export const trackExtensionJourney = (step: 'install' | 'first_login' | 'web_app_conversion', properties: Record<string, any> = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'extension_journey', {
      event_category: 'chrome_extension',
      journey_step: step,
      ...properties,
    });
  }
};

// Freemium to paid conversion tracking
export const trackUpgradeEvent = (fromPlan: string, toPlan: string, trigger: string, properties: Record<string, any> = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'plan_upgrade', {
      event_category: 'subscription',
      from_plan: fromPlan,
      to_plan: toPlan,
      upgrade_trigger: trigger,
      ...properties,
    });
  }
};

// Revenue attribution tracking
export const trackRevenueAttribution = (source: string, feature: string, amount: number, properties: Record<string, any> = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'revenue_attribution', {
      event_category: 'revenue',
      attribution_source: source,
      driving_feature: feature,
      revenue_amount: amount,
      ...properties,
    });
  }
}; 